import React from 'react';
import { useIntl } from 'react-intl';
import { ClockIcon, UsersIcon, CalendarIcon, ArrowTrendingUpIcon, CheckCircleIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/24/solid';

export default function ProgramCardEnrolled({ program, coach, enrollment, className = '' }) {
  const intl = useIntl();
  if (!program || !enrollment) return null;

  const completionPercentage = enrollment.progress_pct || 0;
  const completedTasks = enrollment.completed_task_count || 0;
  const totalTasks = enrollment.total_task_count || 0;

  // Calculate days remaining
  const daysRemaining = enrollment.target_end_on
    ? Math.ceil((new Date(enrollment.target_end_on).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
    : null;

  const isActive = enrollment.status === 'active';
  const isCompleted = enrollment.status === 'completed';

  const statusBadgeClass = isCompleted
    ? 'bg-succession-success text-white'
    : isActive
    ? 'bg-succession-accent text-white'
    : 'bg-succession-text-muted text-white';

  const statusLabel = isCompleted
    ? intl.formatMessage({ id: 'sprints.program_card_enrolled.status.completed' })
    : isActive
    ? intl.formatMessage({ id: 'sprints.program_card_enrolled.status.in_progress' })
    : intl.formatMessage({ id: 'sprints.program_card_enrolled.status.paused' });

  return (
    <div
      className={`group block rounded-2xl overflow-hidden border border-border bg-card transition-all hover:shadow-md ${className}`}
    >
      {/* Hero Image with Overlay */}
      <div className="relative h-48 bg-muted">
        {program.hero_image_url && (
          <img src={program.hero_image_url} alt={program.title} className="w-full h-full object-cover" />
        )}

        {/* Status Badge */}
        <span className={`absolute top-3 right-3 inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold shadow-sm ${statusBadgeClass}`}>
          {statusLabel}
        </span>

        {/* Progress Overlay */}
        {isActive && (
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between text-white text-sm">
                <span className="font-medium">
                  {intl.formatMessage({ id: 'sprints.program_card_enrolled.tasks' }, { completed: completedTasks, total: totalTasks })}
                </span>
                <span className="font-semibold">{completionPercentage}%</span>
              </div>
              <div className="w-full h-2 bg-white/20 rounded-full overflow-hidden">
                <div
                  className="h-full bg-white transition-all"
                  style={{ width: `${completionPercentage}%` }}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-6 space-y-4">
        {/* Title & Summary */}
        <div>
          <h3 className="font-semibold text-[17px] leading-6 mb-1 line-clamp-2 text-gray-900">
            {program.title}
          </h3>
          {program.summary && (
            <p className="text-sm text-gray-600 line-clamp-2">{program.summary}</p>
          )}
        </div>

        {/* Coach Info */}
        {coach && (
          <div className="flex items-center gap-2">
            {coach.avatar_url && (
              <img src={coach.avatar_url} alt={coach.name} className="w-8 h-8 rounded-full" />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate text-gray-900">{coach.name}</p>
              <div className="flex items-center gap-1 text-xs text-gray-600">
                {coach.verified && (
                  <CheckCircleSolidIcon className="w-3 h-3 text-blue-600" />
                )}
                {coach.headline && <span className="truncate">{coach.headline}</span>}
              </div>
            </div>
          </div>
        )}

        {/* Enrollment Stats */}
        <div className="grid grid-cols-2 gap-3 py-3 border-y border-border">
          <div className="flex items-center gap-2 text-sm">
            <CalendarIcon className="w-4 h-4 text-gray-500" />
            <div>
              <p className="text-xs text-gray-500">
                {isCompleted ? intl.formatMessage({ id: 'sprints.program_card_enrolled.stat.completed' }) : intl.formatMessage({ id: 'sprints.program_card_enrolled.stat.days_left' })}
              </p>
              <p className="font-semibold text-gray-900">
                {isCompleted && enrollment.target_end_on
                  ? new Date(enrollment.target_end_on).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                    })
                  : daysRemaining !== null
                  ? intl.formatMessage({ id: 'sprints.program_card_enrolled.days' }, { days: Math.max(0, daysRemaining) })
                  : '—'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 text-sm">
            <ArrowTrendingUpIcon className="w-4 h-4 text-gray-500" />
            <div>
              <p className="text-xs text-gray-500">{intl.formatMessage({ id: 'sprints.program_card_enrolled.stat.progress' })}</p>
              <p className="font-semibold text-gray-900">{completionPercentage}%</p>
            </div>
          </div>
        </div>

        {/* Metadata */}
        <div className="flex items-center gap-4 text-xs text-gray-600">
          {program.duration_days && (
            <div className="flex items-center gap-1">
              <ClockIcon className="w-4 h-4" />
              <span>{intl.formatMessage({ id: 'sprints.program_card.days' }, { days: program.duration_days })}</span>
            </div>
          )}
          {program.avg_rating && (
            <div className="flex items-center gap-1">
              <svg className="w-4 h-4 fill-yellow-400 text-yellow-400" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              <span>{program.avg_rating}</span>
            </div>
          )}
          {program.is_group !== undefined && (
            <div className="flex items-center gap-1">
              <UsersIcon className="w-4 h-4" />
              <span>{program.is_group ? intl.formatMessage({ id: 'sprints.program_card.group' }) : intl.formatMessage({ id: 'sprints.program_card.one_on_one' })}</span>
            </div>
          )}
        </div>

        {/* Action Button - using UI Canon primary button style */}
        <a
          href={`/sprints/enrollments/${enrollment.id}`}
          className="inline-flex items-center justify-center w-full px-5 py-2.5 text-base font-medium rounded-lg bg-succession-primary text-white hover:bg-succession-primary-hover shadow-md hover:shadow-lg transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-succession-focus-ring"
        >
          {isCompleted ? intl.formatMessage({ id: 'sprints.program_card_enrolled.action.review' }) : intl.formatMessage({ id: 'sprints.program_card_enrolled.action.continue' })}
          <ArrowRightIcon className="w-4 h-4 ml-2" />
        </a>
      </div>
    </div>
  );
}
